import React, { useCallback } from 'react';
import { TextField, Autocomplete, Chip } from '@mui/material';
import { ConfigurationData, ElementDefinition } from '../../types';
import { usePreselectedValues } from '../../hooks/usePreselectedValues';
import NumericValueInput from './NumericValueInput';

interface SingleValueInputProps {
  value: string;
  onChange: (value: string) => void;
  elementName: string;
  config?: ConfigurationData | null;
  elementDefinition?: ElementDefinition | null;
  disabled?: boolean;
  placeholder?: string;
}

const SingleValueInput: React.FC<SingleValueInputProps> = ({
  value,
  onChange,
  elementName,
  config,
  elementDefinition,
  disabled = false,
  placeholder = 'Value',
}) => {
  const { options: preselectedValues } = usePreselectedValues(elementName, config);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: string | null) => {
      onChange(newValue ?? '');
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      onChange(newValue);
    },
    [onChange]
  );

  // Use numeric input for numeric element types
  if (elementDefinition?.type === 'numeric') {
    return (
      <NumericValueInput
        value={value}
        onChange={onChange}
        elementDefinition={elementDefinition}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  }

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={preselectedValues}
      freeSolo
      disabled={disabled}
      size="small"
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          sx={{ fontSize: '14px' }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <div>
            {option}
            {preselectedValues.includes(option) && (
              <Chip
                label="suggested"
                size="small"
                variant="outlined"
                sx={{ ml: 1, height: 20, fontSize: '10px' }}
              />
            )}
          </div>
        </li>
      )}
      sx={{ fontSize: '14px' }}
    />
  );
};

export default SingleValueInput;
